import React, { useState, useRef, useEffect } from 'react';
import { 
  MoreVertical, 
  Edit2, 
  Trash2, 
  Plus, 
  Move, 
  Copy,
  Folder,
  FileText,
  Sparkles,
  BookOpen
} from 'lucide-react';

export interface DropdownMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  className?: string;
  disabled?: boolean;
  /** Custom trigger element — replaces the default MoreVertical button */
  trigger?: React.ReactNode;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items,
  className = '',
  disabled = false,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  const updatePosition = () => {
    if (!buttonRef.current || !menuRef.current) return;

    const btnRect = buttonRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.scrollHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let right = viewportWidth - btnRect.right;
    if (right < 200) {
      right = viewportWidth - btnRect.left;
    }

    const spaceBelow = viewportHeight - btnRect.bottom - 8;
    const spaceAbove = btnRect.top - 8;

    let top: number;
    if (spaceBelow >= menuHeight) {
      top = btnRect.bottom + 4;
    } else if (spaceAbove >= menuHeight) {
      top = btnRect.top - menuHeight - 4;
    } else if (spaceBelow >= spaceAbove) {
      top = btnRect.bottom + 4;
    } else {
      top = btnRect.top - Math.min(menuHeight, spaceAbove) - 4;
    }

    if (top < 4) top = 4;

    setPosition({ top, right });
    setIsPositioned(true);
  };

  useEffect(() => {
    if (!isOpen) {
      setIsPositioned(false);
      return;
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    rafRef.current = requestAnimationFrame(() => {
      updatePosition();
    });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);


  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleItemClick = (item: DropdownMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      {/* Three dots button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        disabled={disabled}
        className={`
          p-2 rounded-lg transition-all duration-200
          ${disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-gray-100 active:bg-gray-200'
          }
          ${className}
        `}
        style={{ minWidth: '44px', minHeight: '44px' }}
        aria-label="More actions"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {trigger ?? <MoreVertical className="h-4 w-4 text-gray-600" />}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px] max-w-[240px]"
          style={{
            top: `${position.top}px`,
            right: `${position.right}px`,
            maxHeight: `${Math.min(300, window.innerHeight - position.top - 8)}px`,
            overflowY: 'auto',
            visibility: isPositioned ? 'visible' : 'hidden',
          }}
          role="menu"
          aria-orientation="vertical"
        >
          {items.map((item, index) => {
            // Handle divider
            if (item.id === 'divider' || (!item.label && item.disabled)) {
              return (
                <div
                  key={`divider-${index}`}
                  className="h-px bg-gray-200 my-1"
                  role="separator"
                />
              );
            }
            
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={`
                  w-full flex items-center px-4 py-3 text-sm transition-colors
                  ${item.disabled
                    ? 'opacity-50 cursor-not-allowed text-gray-400'
                    : item.destructive
                    ? 'text-red-600 hover:bg-red-50 active:bg-red-100'
                    : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                  }
                `}
                style={{ minHeight: '44px' }} // Mobile-friendly tap target
                role="menuitem"
              >
                <div className="mr-3 flex-shrink-0">
                  {item.icon}
                </div>
                <span className="text-left flex-1">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Predefined action items for common use cases
export const createEditAction = (onEdit: () => void): DropdownMenuItem => ({
  id: 'edit',
  label: 'Edit',
  icon: <Edit2 className="h-4 w-4" />,
  onClick: onEdit,
});

export const createDeleteAction = (onDelete: () => void): DropdownMenuItem => ({
  id: 'delete',
  label: 'Delete',
  icon: <Trash2 className="h-4 w-4" />,
  onClick: onDelete,
  destructive: true,
});

export const createAddChildAction = (onAddChild: () => void, type: 'folder' | 'card' = 'card'): DropdownMenuItem => ({
  id: 'add-child',
  label: `Add ${type === 'folder' ? 'Subfolder' : 'Card'}`,
  icon: type === 'folder' ? <Folder className="h-4 w-4" /> : <FileText className="h-4 w-4" />,
  onClick: onAddChild,
});

export const createMoveAction = (onMove: () => void): DropdownMenuItem => ({
  id: 'move',
  label: 'Move',
  icon: <Move className="h-4 w-4" />,
  onClick: onMove,
});

export const createDuplicateAction = (onDuplicate: () => void): DropdownMenuItem => ({
  id: 'duplicate',
  label: 'Duplicate',
  icon: <Copy className="h-4 w-4" />,
  onClick: onDuplicate,
});

export const createAICardAction = (onCreateAICard: () => void): DropdownMenuItem => ({
  id: 'ai-card',
  label: 'Add a card using AI',
  icon: <Sparkles className="h-4 w-4" />,
  onClick: onCreateAICard,
});

export const createPracticeCardAction = (onPractice: () => void): DropdownMenuItem => ({
  id: 'practice-card',
  label: 'Practice card',
  icon: <BookOpen className="h-4 w-4" />,
  onClick: onPractice,
});

export const createPracticeFolderAction = (onPractice: () => void): DropdownMenuItem => ({
  id: 'practice-folder',
  label: 'Practice folder',
  icon: <BookOpen className="h-4 w-4" />,
  onClick: onPractice,
});
